import type { ComboboxItem } from "@mantine/core";
import { Flex, Text } from "@mantine/core";
import _ from "lodash";
import { forwardRef, memo, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Autocomplete } from "src/components/shared/MantineCombobox";
import { useGetApi, useTheme } from "src/hooks";
import useSavedCursorPosition from "src/hooks/useSavedCursorPosition";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import { setCellProperty } from "src/redux/slices/generatorSlice";

import { resolveTargetId, resolveTargetIdInSection } from "../hooks/useCopyVal";
import { useDynamicTextWidth } from "../hooks/useDynamicTextWidth";
import { useInputProps } from "../hooks/useInputProps";
import { countryCodeMap } from "./countries";

interface PortCodeResult {
  name: string;
  country: string;
  location: string;
}

interface ItemProps extends ComboboxItem {
  country: string;
  location: string;
}

const isPortCodeResult = (item: unknown): item is PortCodeResult => {
  if (typeof item !== "object" || item === null) {
    return false;
  }

  const parsedItem = item as PortCodeResult;
  return (
    typeof parsedItem.name === "string" &&
    typeof parsedItem.country === "string" &&
    typeof parsedItem.location === "string"
  );
};

const AutoCompleteItem = forwardRef<HTMLDivElement, ItemProps>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
  ({ value, country, location, label, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Flex w="100%" justify="space-between" align="center" gap={8}>
        <Text size="sm" c="text" tt="uppercase">
          {label}
        </Text>
        <Flex gap={4} style={{ whiteSpace: "nowrap" }}>
          <Text size="sm" c="text" tt="uppercase">
            {country}
          </Text>
          <Text size="sm" c="text" tt="uppercase">
            {location}
          </Text>
        </Flex>
      </Flex>
    </div>
  ),
);

interface Props {
  id: string;
  value: string;
  required?: boolean;
  width?: string;
  sectionIndex?: number;
  rowIndex?: number;
  rowUuid?: string;
  subUuid?: string;
  cols?: Record<string, any>;
  portCode?: string;
  exclusive?: string;
  target?: string;
  sharedIndex?: number;
}
const mapper = (data: any) => {
  if (!data?.list) return [];
  return _.uniqWith(data.list, (a, b) => _.isEqual(a, b));
};

const parsePortCode = (code?: string) => {
  if (!code) return { country: "", location: "" };

  const [country = "", ...locationParts] = code.split(" ");
  return {
    country,
    location: locationParts.join(" "),
  };
};

const LocLookup = ({
  id,
  value,
  required = false,
  width,
  sectionIndex,
  rowIndex,
  rowUuid,
  subUuid,
  cols,
  portCode,
  ...cell
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { uppercase } = useSelector(state => state.preferencesSlice);
  const sections = useSelector(state => state.generatorSlice.lbentry.sections);
  const { setValue } = useFormContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    onChange: defaultOnChange,
    ref: addToRefs,
    onKeyDown: handleLogbookKeyDown,
    ...inputProps
  } = useInputProps(id, value, required, cell.exclusive);
  const [searchResults, setSearchResults] = useState<PortCodeResult[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [displayMeta, setDisplayMeta] = useState(() => parsePortCode(portCode));
  const [inputId] = useState(`mantine-input-${id}`);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const minWidth = width ? Number(width) * 10 : 200;
  const countryText = displayMeta.country;
  const locationText = displayMeta.location;
  const rightSectionText = `${countryText} ${locationText}`.trim();
  const rightSectionWidth =
    (rightSectionText.length ? rightSectionText.length * 10 : 36) + 8;
  const {
    dynamicWidth: inputWidth,
    sizerRef,
    updateDynamicWidth,
  } = useDynamicTextWidth({
    enabled: true,
    value: inputProps.value,
    minWidth,
    inputRef,
  });

  useEffect(() => {
    const nextMeta = parsePortCode(portCode);
    setDisplayMeta(prev =>
      prev.country === nextMeta.country && prev.location === nextMeta.location
        ? prev
        : nextMeta,
    );
  }, [portCode]);

  const { refetch } = useGetApi({
    request: "search-port-codes",
    body: {
      params: {
        text: value ?? "",
      },
    },
    fetchOnInit: false,
    mapper,
  });

  // Debounce API call
  useEffect(() => {
    if (!isFocused) {
      setSearchResults([]);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      const fetchResults = async () => {
        const data = await refetch(
          {
            params: {
              text: inputProps.value || "",
            },
          },
          true,
        );
        if (data && Array.isArray(data) && data.length > 0) {
          const parsedData = data.filter(isPortCodeResult);
          setSearchResults(parsedData);
        } else {
          setSearchResults([]);
        }
      };
      void fetchResults();
    }, 300);
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputProps.value, isFocused, refetch]);

  const handleChange = (val: string) => {
    defaultOnChange(val);
    updateDynamicWidth(val, inputRef.current);
  };

  const handleItemSelect = (
    selectedValue: string,
    selectedPort?: PortCodeResult,
  ) => {
    defaultOnChange(selectedValue);
    updateDynamicWidth(selectedValue, inputRef.current);
    if (selectedPort) {
      setDisplayMeta({
        country: selectedPort.country,
        location: selectedPort.location,
      });
    }
    dispatch(setEditing(true));
    setSearchResults(prev => prev.filter(port => port.name === selectedValue));
    if (selectedPort && cols) {
      const currentSection =
        typeof sectionIndex === "number" && sections[sectionIndex]
          ? sections[sectionIndex].shared &&
            typeof cell.sharedIndex === "number" &&
            sections[sectionIndex].sections
            ? sections[sectionIndex].sections?.[cell.sharedIndex]
            : sections[sectionIndex]
          : undefined;
      const countryFieldId =
        resolveTargetId(cell.target, cols) ||
        resolveTargetIdInSection(cell.target, currentSection?.rows);
      const countryFieldExists = !!countryFieldId;

      if (countryFieldExists && cell.target) {
        let countryName =
          countryCodeMap[selectedPort.country] || selectedPort.country;
        countryName = countryName.split(",")[0].trim();
        countryName = countryName.replace(/\s*\([^)]*\)\s*/g, "").trim();

        setValue(countryFieldId, countryName);
        dispatch(setEditing(true));
      }

      const newPortCode = selectedPort
        ? `${selectedPort.country} ${selectedPort.location}`
        : portCode;
      dispatch(
        setCellProperty({
          sectionIndex,
          rowIndex,
          cellId: id,
          rowUuid,
          subUuid,
          property: { portCode: newPortCode },
        }),
      );
    }
  };

  const { handleFocus, handleBlur } = useSavedCursorPosition(id, inputRef);

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e);
    setIsFocused(true);

    setTimeout(() => {
      const inputElement = document
        .querySelector(`.${inputId}`)
        ?.querySelector("input");
      if (inputElement && inputRef.current !== inputElement) {
        (inputRef.current as any) = inputElement;
        updateDynamicWidth(`${inputProps.value ?? ""}`, inputElement);
      }
    }, 0);
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.trim() === "" && portCode !== "") {
      setDisplayMeta({ country: "", location: "" });
      dispatch(
        setCellProperty({
          sectionIndex,
          rowIndex,
          cellId: id,
          rowUuid,
          subUuid,
          property: { portCode: "" },
        }),
      );
    }
    handleBlur(e);
    setIsFocused(false);
  };

  const formattedData = searchResults.map(port => ({
    value: `${port.country} ${port.location}`,
    label: port.name,
    country: port.country,
    location: port.location,
  }));

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        backgroundColor: theme?.colors?.inputBackgroud?.[0] ?? "",
      }}>
      <span
        ref={sizerRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "pre",
          pointerEvents: "none",
        }}>
        {inputProps.value || " "}
      </span>
      <Autocomplete
        id={id}
        className={inputId}
        ref={(el: HTMLInputElement | null) => {
          (inputRef as any).current = el;
          addToRefs(el);
        }}
        value={inputProps.value}
        onChange={handleChange}
        onKeyDown={handleLogbookKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onOptionSubmit={optionValue => {
          const selectedPort = searchResults.find(
            port => `${port.country} ${port.location}` === optionValue,
          );
          const option = formattedData.find(item => item.value === optionValue);
          handleItemSelect(option?.label ?? optionValue, selectedPort);
        }}
        rightSection={
          <Flex gap={4}>
            <Text size="sm" c="text">
              {countryText}
            </Text>
            <Text size="sm" c="text">
              {locationText}
            </Text>
          </Flex>
        }
        rightSectionWidth={rightSectionWidth}
        data={formattedData}
        renderOption={({ option }) => <AutoCompleteItem {...option} />}
        miw={inputWidth}
        placeholder="Search port..."
        filter={({ options, search }) => {
          const searchLower = search.toLowerCase().trim();
          if (!searchLower) return options;
          return options.filter(option => {
            if (!("label" in option)) return true;
            const label = String(option.label ?? "").toLowerCase();
            return label.includes(searchLower);
          });
        }}
        comboboxProps={{ withinPortal: true, zIndex: 2000 }}
        limit={200}
        maxDropdownHeight={220}
        error={inputProps.error}
        styles={{
          input: {
            textTransform: uppercase ? "uppercase" : "none",
            position: "relative",
            backgroundColor: "transparent",
            zIndex: 2,
          },

          root: {
            position: "relative",
          },
        }}
      />
    </div>
  );
};

export default memo(LocLookup);
